import * as React from 'react'
import { useMemo } from 'react'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Field, makeStyles, tokens } from '@fluentui/react-components'
import { CalendarMonthRegular, CalendarWorkWeekRegular } from '@fluentui/react-icons'
import useModules from '../../../hooks/module/useModules'
import useRoles, { rolesTimes } from '../../../hooks/useRoles'
import { useTranslation } from 'react-i18next'
import useUser from '../../../hooks/useUser'
import UserPicker from '../../../components/TagPicker/UserPicker'
import { ModuleType } from '../../../context/GlobalContext'
import AttributesList from '../../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../../hooks/useAxios'
import { SortDirection } from '../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import { BaseOverviewCount, BaseOverviewLineItemProps, parseFilterValuesToParams, useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import {
    BaseIntervalOverviewFilterType,
    BaseOverviewFilterProps,
    generateFromDate,
    generateToDate,
} from '../../../pages/BaseOverview/BaseOverviewFilter'
import { Billable, mapTimeOverviewDtoToMutation, TurnOverItem } from '../../../config/registration/timeConfig'
import DateRange from '../../../components/DatePicker/DateRange'
import BillablePicker from '../../../components/TagPicker/BillablePicker'
import BooleanCombobox from '../../../components/Combobox/BooleanCombobox'
import FilePicker from '../../../components/TagPicker/FilePicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import { TimeMutationDto, TimeOverviewDto } from '../../../config/registrationConfig'
import { MoneyDto } from '../../../config/i18nConfig'
import { BaseOverviewSortingType } from '../../../pages/BaseOverview/BaseSortMenu'
import { multiplyTimeRate } from '../../../helpers/numberHelper'
import { ITimeItemDto } from '../../../config/finance/invoiceConfig'
import TimeListItem from './TimeListItem'
import TimeHeaderItem from './TimeHeaderItem'
import { UserStatus } from '../../../config/userConfig'
import HeaderActionsButtons from './HeaderActionsButtons'
import { DateTime } from 'luxon'
import BaseMutableOverview, {
    BaseMutableOverviewActionButtonProps,
    BaseMutableOverviewProps,
} from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import TimeMutationDialogBody from '../../components/Dialog/Registration/TimeMutationDialogBody'
import { formatIsoDate } from '../../../helpers/dateHelper'
import { useTimeDivisorSetting } from '../../../hooks/file/useTimeDivisorSetting'
import BaseIntervalFilterMenu, { filterTagDayItems } from '../../../pages/BaseOverview/BaseIntervalFilterMenu'

export const useItemStyles = makeStyles({
    listTimeItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) var(--baseOverviewTimeWidth) auto var(--baseOverviewFileWidth) var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
    },
    listTimeItemSelectable: {
        gridTemplateColumns:
            'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) var(--baseOverviewTimeWidth) auto min-content var(--baseOverviewPriceWidth) min-content',
    },
    listTimeItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto auto',
    },
    listTimeItemSmallSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto auto',
    },
    listTimeItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
        gap: tokens.spacingVerticalS,
    },
    listTimeItemTinyBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewPriceWidth)',
        gap: tokens.spacingVerticalS,
    },
    listTimeItemOverflow: {
        overflowWrap: 'anywhere',
    },
    turnoverField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalS,
    },
    billableIconSize: {
        fontSize: tokens.fontSizeBase400,
        paddingTop: '3px',
    },
    colorGreen: {
        color: tokens.colorPaletteGreenForeground1,
    },
    colorRed: {
        color: tokens.colorPaletteRedForeground1,
    },
})

type BaseItem = TimeOverviewDto | ITimeItemDto

export type OverviewItem = {
    // Is calculated during runtime and not returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem &
    TurnOverItem

export const getTimeTurnover = (rate: MoneyDto, item: TurnOverItem): MoneyDto => {
    if (!rate) {
        return undefined
    }

    if (!item || item.billable !== Billable.Billable) {
        return {
            amount: '0.00',
            currency: rate.currency,
        }
    }

    // An item only has the turnover property after the filters have applied during the mounting of this TimeList,
    // therefore calculate it when no turnover has been calculated.
    return multiplyTimeRate(item.duration, rate)
}

type TimeOverviewProps = BaseMutableOverviewProps<ExtendedTimeOverviewDto, TimeFilterType, TimeSortingType, TimeOverviewCount>

export type BaseTimeOverviewProps = Partial<TimeOverviewProps> & {
    baseUrl: string

    fileId?: string
    hideFileField?: boolean
    hideInvoiceField?: boolean
    renderLink?: boolean
}

export type BaseTimeLineOverviewProps = BaseTimeOverviewProps & BaseOverviewLineItemProps<ExtendedTimeOverviewDto, TimeFilterType, TimeSortingType>

export type TimeOverviewCount = BaseOverviewCount & {
    duration: number
    currencies: TimeCurrencyOverviewCount[]
}

export type TimeCurrencyOverviewCount = {
    currency: string
    duration: number
    turnover: string
    invoiceTurnover: string
}

export type ExtendedTimeOverviewDto = TimeOverviewDto & {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
}

export type TimeFilterType = AttributeEntityDto &
    BaseIntervalOverviewFilterType & {
        users?: string[]
        isBilled?: boolean
        files?: string[]
        fileOwners?: string[]
        isInvoiceApproval?: boolean
        invoiceApprovals?: string[]
        billable?: Billable[]
    }

export type TimeSorting = {
    id: string
    date: string
    description: string
    'file.name': string
    'file.reference': string
}

export type TimeSortingType = BaseOverviewSortingType<TimeSorting>

type BaseTimeOverviewFilterProps = BaseTimeOverviewProps & BaseOverviewFilterProps<ExtendedTimeOverviewDto, TimeFilterType, TimeSortingType>

function TimeFilter(props: BaseTimeOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<TimeFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const orientation = useMemo(() => (props.viewportMedium ? 'vertical' : 'horizontal'), [props.viewportMedium])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('users', selectedOptions)}
                />
            </Field>
            <Field label={translate('registration.filter.billable')} orientation={orientation}>
                <BillablePicker
                    selectedOptions={values.billable || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('billable', selectedOptions as Billable[])}
                />
            </Field>
            {!values.isInvoiceApproval && modules.has(ModuleType.Invoices) && (
                <Field label={translate('registration.filter.isBilled')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.isBilled?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) =>
                            updateFieldValue('isBilled', data.checked === 'mixed' ? undefined : data.checked)
                        }
                    />
                </Field>
            )}
            {!props.hideFileField && (
                <Field label={translate('registration.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('registration.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const intervalFilterItems: TimeFilterType[] = [
    {
        from: generateFromDate('lastweek'),
        to: generateToDate('lastweek'),
        tag: 'lastweek',
        icon: <CalendarWorkWeekRegular />,
    },
    {
        from: generateFromDate('week'),
        to: generateToDate('week'),
        tag: 'week',
        icon: <CalendarWorkWeekRegular />,
    },
    {
        from: generateFromDate('month'),
        to: generateToDate('month'),
        tag: 'month',
        icon: <CalendarMonthRegular />,
    },
]

const headerSortingItems: TimeSortingType[] = [
    { property: ['date', 'description', 'id'], direction: SortDirection.Ascending },
    { property: ['date', 'description', 'id'], direction: SortDirection.Descending, default: true },
    { property: ['description', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.descriptionaz' },
    { property: ['description', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.descriptionza' },
    { property: ['file.name', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.fileaz' },
    { property: ['file.name', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.fileza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: TimeOverviewProps) {
    const overviewStyles = useOverviewStyles()

    return (
        <div className={overviewStyles.gridGapXS}>
            <BaseIntervalFilterMenu {...props} filterItems={intervalFilterItems} />
        </div>
    )
}

export default function BaseTimeOverview(props: BaseTimeOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const roles = useRoles()
    const timeDivisorSetting = useTimeDivisorSetting()
    const { currentUser } = useUser()

    const defaultFilterValues = useMemo<TimeFilterType>(
        () => ({
            from: '',
            to: '',
            users: roles.any(rolesTimes) && currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            attributes: [],
            query: '',
            tag: filterTagDayItems.find(tag => tag.from === DateTime.local().toISODate())?.tag ?? 'week',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (_column?: number, filterValues?: TimeFilterType): Promise<TimeOverviewCount> => {
        return axios.get('/time/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        _column?: number,
        filterValues?: TimeFilterType,
        sortingValues?: TimeSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedTimeOverviewDto[]> => {
        return axios
            .get('/time', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<OverviewItem[]>): ExtendedTimeOverviewDto[] =>
                (response?.data || []).map((item: OverviewItem) => ({
                    ...item,
                    turnover: getTimeTurnover(item.rate, item),
                    invoiceTurnover: item.price && {
                        amount: item.price.subtotal,
                        currency: item.price.currency,
                    },
                })),
            )
    }

    return (
        <BaseMutableOverview<
            ExtendedTimeOverviewDto,
            TimeFilterType,
            TimeSortingType,
            TimeOverviewCount,
            BaseMutableOverviewActionButtonProps<ExtendedTimeOverviewDto>,
            any,
            TimeMutationDto
        >
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={TimeHeaderItem}
            listItemContent={(props: BaseTimeLineOverviewProps) => <TimeListItem {...props} />}
            listItemClassnames={props => [
                props.onItemSelected ? itemStyles.listTimeItemSelectable : itemStyles.listTimeItem,
                props.viewportMedium && (props.onItemSelected ? itemStyles.listTimeItemSmallSelectable : itemStyles.listTimeItemSmall),
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            headerActionsButtons={HeaderActionsButtons}
            filterContent={TimeFilter}
            filterFromJson={values => ({
                ...values,
                from: values.tag ? generateFromDate(values.tag) : values.from,
                to: values.tag ? generateToDate(values.tag) : values.to,
            })}
            filterToJson={values => ({
                ...values,
                from: values.tag ? '' : values.from,
                to: values.tag ? '' : values.to,
                icon: undefined,
                title: undefined,
            })}
            inputPlaceholderKey={'time.filter.search'}
            mutationDialogFormProps={item => ({
                initialValues: item
                    ? mapTimeOverviewDtoToMutation(item)
                    : {
                          description: undefined,
                          fileId: props.fileId,
                          userId: currentUser?.id,
                          billable: undefined,
                          date: formatIsoDate(new Date()),
                          duration: timeDivisorSetting.initialDuration,
                          currency: undefined,
                          rate: undefined,
                          category: undefined,
                      },
                onSubmit: () => {},
            })}
            mutationDialogBodyProps={item => ({
                item,
            })}
            mutationDialogBody={TimeMutationDialogBody}
            {...props}
        />
    )
}
